import { VirtualScroller, VirtualScrollerState } from "../list/UI/timeline/virtual-scroller";

export const INITIAL_SCROLLER_STATE: VirtualScrollerState = {
  virtualItems: [],
  totalHeight: 0,
};

export interface ScrollerSyncOptions<T> {
  viewport: HTMLElement | undefined;
  items: T[];
  onStateChange: (state: VirtualScrollerState) => void;
  estimateItemHeight: (index: number) => number;
}

/**
 * Small state-machine wrapper around VirtualScroller that both the timeline
 * and the importer reuse. It handles:
 *  - first-time setup when the viewport/content elements become available,
 *  - re-setup if the viewport element is swapped between renders,
 *  - incremental item updates,
 *  - cleanup on teardown.
 *
 * Each caller owns its own state field and own onStateChange callback (the
 * timeline, for example, also publishes a broker event inside it).
 */
export class ScrollerController<T> {
  private _scroller?: VirtualScroller<T>;
  private _cleanup?: () => void;
  private _lastViewport?: HTMLElement;

  /** Setup or update the scroller with the current items. No-op until the
   * viewport and content elements exist. Detects viewport swaps and re-setups. */
  sync({ viewport, items, onStateChange, estimateItemHeight }: ScrollerSyncOptions<T>): VirtualScrollerState {
    if (!viewport) {
      return this._scroller?.getState() ?? INITIAL_SCROLLER_STATE;
    }

    const viewportChanged = this._scroller && viewport !== this._lastViewport;
    if (viewportChanged) {
      this.teardown();
    }

    if (!this._scroller) {
      this._scroller = new VirtualScroller<T>({
        items,
        estimateItemHeight,
      });
      this._cleanup = this._scroller.setup(viewport, onStateChange);
    } else {
      this._scroller.updateItems(items, onStateChange);
    }

    this._lastViewport = viewport;
    return this._scroller.getState();
  }

  teardown() {
    this._cleanup?.();
    this._cleanup = undefined;
    this._scroller = undefined;
    this._lastViewport = undefined;
  }

  scrollToIndex(index: number, behavior?: ScrollBehavior) {
    this._scroller?.scrollToIndex(index, behavior);
  }

  /** Measure the real height of a rendered item so variable-height rows
   * (e.g. very tall visits) get accurate offsets instead of the estimate. */
  measureElement(element: HTMLElement) {
    this._scroller?.measureElement(element);
  }
}
